'use client';

import React, { useState } from 'react'
import Link from 'next/link'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  ArrowRight, Sparkles, ShoppingBag, Share2, Users, QrCode, 
  Flame, DollarSign, CheckCircle2, ChevronDown, Heart, MessageSquare
} from 'lucide-react'
import { MarketingNavbar } from '@/components/layout/marketing-navbar'
import { MarketingFooter } from '@/components/layout/marketing-footer'
import { GeometricBackground } from '@/components/ui/geometric-background'
import { ShinyButton } from '@/components/ui/shiny-button'
import { DashboardPreview } from '@/components/ui/dashboard-animation'

export default function CreatorsClient() {
  const [openFaq, setOpenFaq] = useState<number | null>(null)

  const faqs = [
    {
      q: "Can I migrate my existing Linktree or Beacons page to Lenklyst?",
      a: "Yes, easily! You can recreate your link profile in our visual builder in less than 3 minutes. Plus, we don't lock basic features like background uploading or avatar customization behind upgrade screens."
    },
    {
      q: "How do storefront payouts work on Lenklyst?",
      a: "Lenklyst integrates directly with Stripe. When someone buys a digital product from your store, payments are deposited directly into your bank account. We only charge a minimal 5% platform fee, allowing you to keep 95% of your earnings."
    },
    {
      q: "How does the milestone referral system help me grow?",
      a: "You can lock premium files, coupon codes, or private links behind a referral goal. Your followers get their own unique invite link, and they share it with friends to unlock the reward. This turns your audience into an organic referral growth loop!"
    },
    {
      q: "Is there a limit on how many links or QR codes I can make?",
      a: "Our free tier includes 50 smart links, 20 dynamic QR codes, and 1 complete bio-page storefront. You only upgrade if you need multiple bio profiles, detailed custom analytics, or heavier team collaboration capabilities."
    }
  ]

  return (
    <div className="relative min-h-screen bg-black overflow-x-clip selection:bg-primary/30 text-white font-sans">
      <GeometricBackground />
      <MarketingNavbar />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-6 max-w-[1400px] mx-auto z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center min-h-[70vh]">
          {/* Left Column: Hero Copy */}
          <div className="lg:col-span-7 space-y-6 flex flex-col items-start text-left">
            <span className="inline-flex items-center gap-2 px-3 py-1 bg-primary/20 text-primary text-[10px] font-bold tracking-widest uppercase rounded-full">
              <Sparkles className="w-3.5 h-3.5" /> For Content Creators &amp; Influencers
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-7xl font-bold tracking-tighter uppercase leading-[1.05]">
              Own your link.<br />
              <span className="text-gray-500">Own your audience.</span><br />
              Keep 95% revenue.
            </h1>
            <p className="text-base sm:text-lg text-white/50 font-light max-w-xl leading-relaxed">
              Consolidate your profiles, digital storefront, waitlists, and viral giveaways into a single, high-converting branded bio page. Avoid massive platform fees.
            </p>
            <div className="flex flex-col sm:flex-row items-center gap-4 w-full sm:w-auto">
              <Link href="/auth/sign-up" className="w-full sm:w-auto">
                <ShinyButton className="w-full justify-center py-4 px-8 text-base">
                  Claim Your Bio Page <ArrowRight className="w-4 h-4 ml-1" />
                </ShinyButton>
              </Link>
              <a href="#benefits" className="w-full sm:w-auto text-center py-3.5 px-8 rounded-full border border-white/10 text-white/60 hover:text-white hover:border-white/20 transition-all font-semibold text-sm">
                Explore Benefits
              </a>
            </div>
          </div>

          {/* Right Column: Dashboard Preview */}
          <div className="lg:col-span-5 flex justify-center lg:justify-end relative">
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-80 h-80 bg-primary/10 rounded-full blur-[100px] pointer-events-none" />
            <div className="w-full max-w-[460px] relative z-10">
              <DashboardPreview sectionId="bio" className="shadow-[0_0_50px_rgba(59,130,246,0.15)]" />
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Bento Section */}
      <section id="benefits" className="py-24 border-t border-white/5 relative z-10">
        <div className="max-w-[1200px] mx-auto px-6">
          <div className="space-y-4 mb-16 text-center">
            <span className="text-[10px] font-bold tracking-[0.2em] text-primary uppercase">Why Creators Choose Us</span>
            <h2 className="text-3xl sm:text-5xl font-bold uppercase tracking-tighter">
              Everything in one beautiful link.
            </h2>
            <p className="text-sm sm:text-base text-white/40 max-w-lg mx-auto font-light">
              Skip paying for multiple subscriptions. Lenklyst replaces your bio page, digital store, and giveaway tools.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Benefit Card 1 */}
            <div className="p-8 bg-white/[0.01] hover:bg-white/[0.02] border border-white/5 rounded-[2rem] transition-all flex flex-col justify-between space-y-6">
              <div className="space-y-4">
                <div className="p-3 bg-white/5 rounded-2xl w-fit">
                  <Share2 className="w-6 h-6 text-primary" />
                </div>
                <h3 className="text-xl font-bold uppercase tracking-tight">Interactive Bio Customizer</h3>
                <p className="text-sm text-white/50 font-light leading-relaxed">
                  Design a landing page that fits your aesthetic. Upload custom banners, select typography, and add dynamic widgets. No forced brand logos on premium layouts.
                </p>
              </div>
              <ul className="space-y-2 border-t border-white/[0.04] pt-6">
                {['Unlimited custom bio buttons', 'Background animations & theme library', 'Direct social feeds integration'].map((item) => (
                  <li key={item} className="flex items-center gap-2 text-xs text-white/75 font-light">
                    <CheckCircle2 className="w-3.5 h-3.5 text-primary flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* Benefit Card 2 */}
            <div className="p-8 bg-white/[0.01] hover:bg-white/[0.02] border border-white/5 rounded-[2rem] transition-all flex flex-col justify-between space-y-6">
              <div className="space-y-4">
                <div className="p-3 bg-white/5 rounded-2xl w-fit">
                  <ShoppingBag className="w-6 h-6 text-primary" />
                </div>
                <h3 className="text-xl font-bold uppercase tracking-tight">Direct Storefront Payments</h3>
                <p className="text-sm text-white/50 font-light leading-relaxed">
                  Stop losing large commissions to other marketplaces. Sell digital products, templates, ebooks, or appointments directly from your bio link.
                </p>
              </div>
              <ul className="space-y-2 border-t border-white/[0.04] pt-6">
                {['Only 5% platform fee', 'Direct payouts via Stripe Connect', 'Instant digital file delivery'].map((item) => (
                  <li key={item} className="flex items-center gap-2 text-xs text-white/75 font-light">
                    <CheckCircle2 className="w-3.5 h-3.5 text-primary flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* Benefit Card 3 */}
            <div className="p-8 bg-white/[0.01] hover:bg-white/[0.02] border border-white/5 rounded-[2rem] transition-all flex flex-col justify-between space-y-6">
              <div className="space-y-4">
                <div className="p-3 bg-white/5 rounded-2xl w-fit">
                  <Users className="w-6 h-6 text-primary" />
                </div>
                <h3 className="text-xl font-bold uppercase tracking-tight">Milestone Referrals &amp; Giveaways</h3>
                <p className="text-sm text-white/50 font-light leading-relaxed">
                  Boost your email subscriber list and social accounts. Motivate your followers to refer their friends to unlock discounts, premium guides, or physical products.
                </p>
              </div>
              <ul className="space-y-2 border-t border-white/[0.04] pt-6">
                {['Custom milestones with unlock keys', 'Anti-fraud email validation checks', 'Automatic invite link generators'].map((item) => (
                  <li key={item} className="flex items-center gap-2 text-xs text-white/75 font-light">
                    <CheckCircle2 className="w-3.5 h-3.5 text-primary flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* Benefit Card 4 */}
            <div className="p-8 bg-white/[0.01] hover:bg-white/[0.02] border border-white/5 rounded-[2rem] transition-all flex flex-col justify-between space-y-6">
              <div className="space-y-4">
                <div className="p-3 bg-white/5 rounded-2xl w-fit">
                  <QrCode className="w-6 h-6 text-primary" />
                </div>
                <h3 className="text-xl font-bold uppercase tracking-tight">Dynamic Vector QR Codes</h3>
                <p className="text-sm text-white/50 font-light leading-relaxed">
                  Bridge physical and digital marketing. Generate high-quality vector dynamic QRs that can be printed on stickers, posters, shirts, and packaging.
                </p>
              </div>
              <ul className="space-y-2 border-t border-white/[0.04] pt-6">
                {['Update destination link anytime', 'Scan counter & location logs', 'High-resolution SVG vector outputs'].map((item) => (
                  <li key={item} className="flex items-center gap-2 text-xs text-white/75 font-light">
                    <CheckCircle2 className="w-3.5 h-3.5 text-primary flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 border-t border-white/5 relative z-10 bg-black/40">
        <div className="max-w-[800px] mx-auto px-6">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-3xl sm:text-5xl font-bold uppercase tracking-tighter">
              Creator FAQs
            </h2>
            <p className="text-xs sm:text-sm text-white/40 max-w-sm mx-auto font-light">
              Everything you need to know about setting up your link-in-bio page.
            </p>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, idx) => {
              const isOpen = openFaq === idx
              return (
                <div 
                  key={idx}
                  className="bg-white/[0.01] hover:bg-white/[0.02] border border-white/5 rounded-2xl overflow-hidden transition-all duration-300"
                >
                  <button
                    onClick={() => setOpenFaq(isOpen ? null : idx)}
                    className="w-full flex items-center justify-between p-5 text-left focus:outline-none"
                  >
                    <span className="text-sm font-bold uppercase tracking-tight text-white/90">
                      {faq.q}
                    </span>
                    <ChevronDown className={`w-4 h-4 text-white/40 transition-transform duration-300 ${isOpen ? "rotate-180 text-primary" : ""}`} />
                  </button>
                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                      >
                        <div className="px-5 pb-5 pt-1 text-xs sm:text-sm text-white/50 leading-relaxed font-light border-t border-white/[0.02]">
                          {faq.a}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              )
            })}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-32 border-t border-white/5 relative z-10 overflow-hidden text-center">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[300px] bg-primary/10 rounded-full blur-[160px] pointer-events-none" />
        <div className="max-w-[1400px] mx-auto px-6 relative space-y-8">
          <h2 className="text-4xl sm:text-6xl font-bold tracking-tighter uppercase">
            Start owning your online space today.
          </h2>
          <p className="text-sm sm:text-base text-white/40 max-w-md mx-auto font-light leading-relaxed">
            Join thousands of creators using Lenklyst to build bio pages, host storefronts, and run referral marketing.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link href="/auth/sign-up">
              <ShinyButton className="px-10 py-4 text-sm font-bold">
                Get Started Free <ArrowRight className="w-4 h-4 ml-1" />
              </ShinyButton>
            </Link>
            <Link href="/pricing" className="text-white/60 hover:text-white border border-white/10 px-8 py-3.5 rounded-full text-sm font-semibold hover:border-white/20 transition-all">
              Compare Premium Tiers
            </Link>
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  )
}
